import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import i18next from 'i18next';

import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';
import { useMediaQuery } from 'react-responsive';
import api from '../../../services/api';
import getValidationErrors from '../../../utils/getValidationErrors';

import BackgroundBLue from '../../../assets/images/backgroundSignIn.jpg';

import {
  FuzzyLogoNewOrange,
  MailIcon,
  UserCircleIcon,
} from '../../../assets/svg/SVGComponents';

import Input from '../../../components/Input';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import PrivacyPolicyModal from '../../../modals/PrivacyPolicyModal';

import {
  Container,
  Content,
  SideContent,
  Footer,
  ImageArea,
  TipBackground,
  LanguageButtons,
  TopContent,
  SignUpSuccess,
} from './styles';
import { useToast } from '../../../hooks/toast';
import { useModal } from '../../../hooks/modal';
import CheckboxUnform from '../../../components/CheckboxUnform';

interface ISignUpFormData {
  name: string;
  email: string;
  privacy_policy: boolean;
  cookies_policy: boolean;
}

const SignUp: React.FC = () => {
  const { handleModal } = useModal();
  const formRef = useRef<FormHandles>(null);
  const [captcha, setCaptcha] = useState<string | null>('');
  const [isLoading, setIsLoading] = useState(false);
  const [signedUpEmail, setSignedUpEmail] = useState<string | null>(null);

  const { t } = useTranslation();
  const { addToast } = useToast();
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (isTabletOrMobile || isPortrait) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [isPortrait, isTabletOrMobile]);

  const recaptchaRef = useRef(null);

  const handleSubmit = useCallback(
    async (data: ISignUpFormData) => {
      formRef.current?.setErrors({});
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: Unreachable code error
      recaptchaRef.current?.execute();

      const formattedFormData = {
        name: data.name,
        email: data.email,
        privacy_policy: data.privacy_policy,
        cookies_policy: data.privacy_policy,
        recaptcha: captcha,
      };

      try {
        setIsLoading(true);
        const signUpSchema = Yup.object().shape({
          name: Yup.string().required(t('yup_errors.name_required')),
          email: Yup.string()
            .required(t('yup_errors.email_required'))
            .email(t('yup_errors.invalid_email')),
          privacy_policy: Yup.boolean().oneOf(
            [true],
            t('yup_errors.privacy_required'),
          ),
          cookies_policy: Yup.boolean()
            .oneOf([true], t('yup_errors.privacy_required'))
            .required(t('yup_errors.privacy_required')),
        });

        await signUpSchema.validate(formattedFormData, { abortEarly: false });

        await api.post('/users/signup', {
          name: data.name,
          email: data.email,
          privacy_policy: data.privacy_policy,
          cookies_policy: data.privacy_policy,
        });

        setSignedUpEmail(data.email);
      } catch (err) {
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 403 || err.response?.status === 409) {
            addToast({
              type: 'error',
              expression: 'Ops.',
              description: t('toasts.errors.signup_email_already_exists'),
            });
          } else {
            addToast({
              type: 'error',
              expression: 'Ops.',
              description: t('toasts.errors.signup_request_fail'),
            });
          }
        }

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [captcha, t, addToast],
  );

  const handleLanguageChange = useCallback((lang_code: string) => {
    i18next.changeLanguage(lang_code);
  }, []);

  const handleCaptcha = useCallback(async (token: string | null) => {
    setCaptcha(token);
  }, []);

  if (!process.env.REACT_APP_RECAPTCHA_KEY) {
    throw new Error(
      'A chave do ReCAPTCHA não está definida nas variáveis de ambiente.',
    );
  }

  const handlePolicyPrivacyModal = useCallback(() => {
    handleModal(<PrivacyPolicyModal />, {
      pathname: '/signup',
      state: {
        'modal-type': 'privacy-policy',
        'modal-source': 'signup',
      },
    });
  }, [handleModal]);

  return (
    <Container>
      <Content $isMobile={isMobile}>
        <FuzzyLogoNewOrange />

        {signedUpEmail ? (
          <SignUpSuccess>
            <span className="success-icon">✉️</span>
            <h2>Confirme seu email</h2>
            <p>
              Enviamos um link de confirmação para{' '}
              <strong>{signedUpEmail}</strong>. Clique no link para ativar sua
              conta e fazer o primeiro acesso.
            </p>
            <button
              type="button"
              className="resend-link"
              onClick={() => setSignedUpEmail(null)}
            >
              Usar outro email
            </button>
          </SignUpSuccess>
        ) : (
          <Form ref={formRef} onSubmit={handleSubmit}>
            <h1>
              <Trans i18nKey="h1.teacher_signup" />
            </h1>

            <span>{t('labels.teacher_name')}</span>
            <Input
              name="name"
              type="text"
              placeholder={t('placeholders.full_name')}
              icon={UserCircleIcon}
            />

            <span>{t('labels.email')}</span>
            <Input
              name="email"
              type="text"
              placeholder={t('placeholders.email')}
              icon={MailIcon}
              autoComplete="email"
            />

            <CheckboxUnform
              name="privacy_policy"
              isBoolean
              className="checkbox"
            >
              {t('navlinks.accept_policies_and_terms')}
              <button
                type="button"
                className="policy-button"
                aria-label="Privacy Policy"
                onClick={() => handlePolicyPrivacyModal()}
              >
                {t('navlinks.policies_and_terms')}
              </button>
            </CheckboxUnform>

            <PrimaryButton type="submit" loading={isLoading}>
              {t('buttons.signup')}
            </PrimaryButton>
            <Footer>
              <Link to="/">{t('navlinks.signin')}</Link>

              <ReCAPTCHA
                className="captcha"
                ref={recaptchaRef}
                size="invisible"
                sitekey={process.env.REACT_APP_RECAPTCHA_KEY}
                onChange={handleCaptcha}
              />
            </Footer>
          </Form>
        )}

        <footer />
      </Content>
      {!isMobile && (
        <SideContent>
          <ImageArea>
            <img src={BackgroundBLue} alt="fuzzy-logo" />
            <TopContent>
              <LanguageButtons>
                <button
                  type="button"
                  className={
                    i18next.language === 'pt-BR' ? 'active-language' : ''
                  }
                  onClick={() => handleLanguageChange('pt-BR')}
                >
                  PT
                </button>
                |
                <button
                  type="button"
                  className={i18next.language === 'en' ? 'active-language' : ''}
                  onClick={() => handleLanguageChange('en')}
                >
                  EN
                </button>
              </LanguageButtons>
              <TipBackground>
                <img
                  src="https://fuzzyplaydev.s3.us-east-2.amazonaws.com/backgroundTip.png"
                  alt="tip-bg"
                />
              </TipBackground>
            </TopContent>
          </ImageArea>
        </SideContent>
      )}
    </Container>
  );
};

export default SignUp;
